/**
 * In-match levels.
 *
 * A cosmetic progression over the in-play score: a milestone the HUD
 * celebrates and a number two players compare afterwards. Levels **pay
 * nothing, change nothing and decide nothing** — the deck is the same, the
 * clock is the same, and ranking is still score, then the shorter clock, then
 * the earlier submission. A level-9 run with the lower score loses to a
 * level-3 run, and that is not a bug to be fixed later.
 *
 * Shared with every Chain Games title, and pure on purpose: `src/game` never
 * imports the renderer, so the server replays this exact function to decide the
 * level it records. A client claiming level 99 is ignored because the server
 * never asks it.
 *
 * ## The curve
 *
 *   threshold(n) = LEVEL_BASE × (n − 1) × (n + 3),  n ≥ 2
 *
 * Front-loaded and then stretched: the first levels arrive quickly so an early
 * run feels like it is going somewhere, and later ones cost progressively more
 * so the number keeps meaning something. Uncapped.
 *
 * ## Why `LEVEL_BASE` is 100 here, and what it does not achieve
 *
 * The brief says to tune the base so a strong three-minute run lands around 9
 * or 10 and an average one around 5 or 6. Measured against 908 real results:
 * p25 5,450 · median 6,613 · p75 7,426 · p95 8,570 · best 10,545.
 *
 * At 100 — the shared value — the median run reaches **level 7**, p95 reaches
 * **8**, and the best score on record reaches **9**. So the top of the target
 * is hit and the middle sits one or two levels high.
 *
 * That gap cannot be closed by moving the base, and it is worth saying why
 * rather than leaving the next person to rediscover it. Spanning level 6 to
 * level 10 on this curve needs the top score to be **2.6x** the average one.
 * In 21 Wild it is **1.6x**: a fixed three-minute clock and a time bonus that
 * scales with cards remaining pull every finished round toward the same band.
 * Whatever base is chosen, the distribution is too narrow to cover both ends of
 * that range — dropping the median to 6 costs the top of the ladder, which is
 * the half players notice.
 *
 * So the shared 100 stays, which has the second advantage of making a level in
 * this game mean the same as a level in the others.
 */

/** Scales the whole ladder. Shared across titles — see the note above. */
export const LEVEL_BASE = 100;

/**
 * The score at which level `n` is reached.
 *
 * 0 below level 2, because everybody starts at 1 and there is nothing to earn.
 */
export function levelThreshold(n: number): number {
  if (!Number.isFinite(n) || n <= 1) return 0;
  return LEVEL_BASE * (n - 1) * (n + 3);
}

/**
 * The level a score has reached.
 *
 * Closed form first — inverting `(n−1)(n+3)` gives `n = √(4 + score/base) − 1`
 * — then walked onto the integer boundary in both directions. The walk is the
 * point: a score sitting exactly on a threshold is the one case anybody will
 * test, and it is exactly where a float can land a hair under and report the
 * level below. Two loops that each run at most once cost nothing and remove
 * the whole class of error.
 */
export function levelFor(score: number): number {
  if (!Number.isFinite(score) || score <= 0) return 1;

  let n = Math.floor(Math.sqrt(4 + score / LEVEL_BASE) - 1);
  if (n < 1) n = 1;
  while (levelThreshold(n + 1) <= score) n++;
  while (n > 1 && levelThreshold(n) > score) n--;
  return n;
}

/** What the next level costs, for a progress bar. */
export function nextThreshold(level: number): number {
  return levelThreshold(level + 1);
}

/**
 * How far this score is through its current level, 0..1.
 *
 * The HUD pill's fill. Clamped at both ends so a score past the last computed
 * threshold — there is no last threshold, but a caller may pass anything —
 * cannot produce a bar wider than the pill.
 */
export function levelProgress(score: number): number {
  const level = levelFor(score);
  const floor = levelThreshold(level);
  const ceiling = levelThreshold(level + 1);
  if (ceiling <= floor) return 0;
  return Math.min(1, Math.max(0, (score - floor) / (ceiling - floor)));
}
